import { useEffect, useState } from 'react';
import DateSelector from '../dateSelector';
import EventResume from '../eventResume';
import dayjs from 'dayjs';
import SchedulePolicyModal from '../schedulePolicyModal';
import { scheduleEvent } from '../../apis/sessionsApi';
import { openNotification } from '../notifications';
import { getIsHoursToCancelFeatureOn, getIsPenalizationFeatureOn } from '../../config';
import Messages from '../../utils/Messages';
import { loadEvents } from './loadNextSessions';
import Features from '../../enums/features';
import { Spin, Button, Modal, notification } from 'antd';
import utc from 'dayjs/plugin/utc';
import { fetchBranchConfig } from '../../apis/branchApi';
import BranchConfig from '../../enums/branch_config';
import { activePlan } from '../../apis/plansApi';
import { jwtDecode } from 'jwt-decode';
import EventEditModal from '../EventEditModal';
import { deleteEvent } from '../../apis/eventsApi';

dayjs.extend(utc);

const NextSessions = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteScope, setDeleteScope] = useState('one');
  
  const isHoursToCancelFeatureOn = getIsHoursToCancelFeatureOn();
  const isPenalizationFeatureOn = getIsPenalizationFeatureOn();
  
  const [isSeePastEventsEnabled, setIsSeePastEventsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [maxAllowedDate, setMaxAllowedDate] = useState(null);
  const [minAllowedDate, setMinAllowedDate] = useState(null);

  useEffect(() => {
    const setDateLimits = async () => {
      try {
        const tz = await fetchBranchConfig(BranchConfig.TIMEZONE);
        const newFromDate = dayjs().tz(tz).startOf("day");
        const newToDate = dayjs().tz(tz).add(6, 'day').endOf("day");
        setFromDate(newFromDate);
        setToDate(newToDate);

        const access_token = localStorage.getItem('access_token');
        const decodedToken = jwtDecode(access_token);
        const userId = decodedToken.sub;
        const response = await activePlan(userId);
        if (response.status !== 200) {
          setMaxAllowedDate(newToDate);
          setMinAllowedDate(newFromDate);
          return;
        }
        const weeks_ahead = await fetchBranchConfig(BranchConfig.WEEKS_AHEAD_PLAN_X + response.data.plan_id);

        // Calculate the maximum allowed date based on the current date
        //const maxDate = dayjs().add(response.allowedWeeksAhead * 7, 'day');
        const maxDate = dayjs().tz(tz).add(6, 'day').add(weeks_ahead * 6, 'day').endOf("day");
        setMaxAllowedDate(maxDate);
        setMinAllowedDate(newFromDate);
      } catch (error) {
        console.error("Error setting date limits:", error);
      }
    };

    setDateLimits();
  }, []);

  useEffect(() => {
    if (!fromDate || !toDate) return; // Wait until both dates are initialized

    setIsLoading(true);
    setSelectedDate(null);
    const features = JSON.parse(localStorage.getItem('user_features') || '{}');
    const passEventsFeature = features[Features.SEE_PAST_EVENTS] || false;
    setIsSeePastEventsEnabled(passEventsFeature);
    const fetchAndSetEvents = async () => {
      // TODO: load branch from authenticated user
      const branchId = 1; // Replace with actual branch ID from user context or props
      const loadedEvents = await loadEvents(branchId, fromDate, toDate, false, !passEventsFeature);
      setEvents(loadedEvents);
      setIsLoading(false);
    };
    fetchAndSetEvents();
  }, [fromDate, toDate, isSeePastEventsEnabled]);

  useEffect(() => {
    let filtered = events;

    // If a date is selected, further filter by that date
    if (selectedDate) {
      setIsLoading(true);
      filtered = filtered.filter(event =>
        event.start_date && dayjs.utc(event.start_date).tz(dayjs.tz.guess()).isSame(selectedDate, 'day')
      );
      setIsLoading(false);
    }

    setFilteredEvents(filtered);
  }, [selectedDate, events, isSeePastEventsEnabled]);

  
  const handleScheduleEvent = async (event) => {
    const result = await scheduleEvent(event);
    setShowScheduleModal(false);

    const userLanguage = navigator.language || navigator.userLanguage; // Detect browser language
    if(result.success) {
      setShowScheduleModal(false);
      openNotification(
        "success",
        "",
        Messages.getMessage("SUCCESS_SCHEDULE", userLanguage)
      );
    }else {
      const messageCode = result.error.response?.data?.responseCode || "UNKNOWN_ERROR";
      openNotification('error',
        'Error al agendar',
        Messages.getMessage(messageCode, userLanguage),
      );
    }
  }

  const handleSchedule = (event) => {
    if (isPenalizationFeatureOn || isHoursToCancelFeatureOn) {
      setSelectedEvent(event);
      setShowScheduleModal(true);
    } else {
      handleScheduleEvent(event);
    }
  };

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setShowEditModal(true);
  };

  const handleDelete = (event) => {
    setSelectedEvent(event);
    setDeleteScope('one');
    setShowDeleteModal(true);
  };

  const handleEditSuccess = async () => {
    setShowEditModal(false);
    const branchId = 1;
    const loadedEvents = await loadEvents(branchId, fromDate, toDate, false, !isSeePastEventsEnabled);
    setEvents(loadedEvents);
  };

  const confirmDelete = async () => {
    if (!selectedEvent) return;
    try {
      // For is_edited events, backend needs original_start_date to look up the exception
      const occurrenceDate = selectedEvent.is_edited
        ? (selectedEvent.original_start_date
            ? (typeof selectedEvent.original_start_date === 'string'
                ? selectedEvent.original_start_date
                : selectedEvent.original_start_date?.format?.('YYYY-MM-DD HH:mm:ss'))
            : selectedEvent.start_date?.format('YYYY-MM-DD HH:mm:ss'))
        : selectedEvent.start_date?.format('YYYY-MM-DD HH:mm:ss');
      await deleteEvent(
        selectedEvent.event_id,
        deleteScope,
        occurrenceDate,
        selectedEvent.rule_id,
        selectedEvent.is_unique || false,
        selectedEvent.is_edited || false
      );
      notification.success({ message: 'Evento eliminado' });
      setShowDeleteModal(false);
      // Refresh events list
      const branchId = 1;
      const loadedEvents = await loadEvents(branchId, fromDate, toDate, false, !isSeePastEventsEnabled);
      setEvents(loadedEvents);
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error al eliminar evento',
        description: err.response?.data?.error || err.message,
      });
    }
  };

  const moveWeek = (direction) => {
    // direction: -1 for past, 1 for future
    setFromDate(prev => prev.add(7 * direction, 'day'));
    setToDate(prev => prev.add(7 * direction, 'day'));
  };

  const canMoveNextWeek = maxAllowedDate ? toDate.isBefore(maxAllowedDate) : false;
  const canMovePreviousWeek = minAllowedDate ? fromDate.isAfter(minAllowedDate) : false;

  return (
    fromDate && toDate && (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {(isSeePastEventsEnabled || canMovePreviousWeek) && (
            <button
              style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => moveWeek(-1)}
              aria-label="Semana anterior"
            >
              ◀
            </button>
          )}
          <DateSelector
            fromDate={fromDate}
            toDate={toDate}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
          />
          {(isSeePastEventsEnabled || canMoveNextWeek) && (
            <button
              style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => moveWeek(1)}
              aria-label="Semana siguiente"
            >
              ▶
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="text-center mt-3">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {filteredEvents.map((event) => (
              <EventResume key={`${event.id}-${event.start_date}`} event={event} handleSchedule={handleSchedule} onEdit={handleEdit} onDelete={handleDelete} />
            ))}

            <SchedulePolicyModal
              show={showScheduleModal}
              event={selectedEvent}
              onClose={() => setShowScheduleModal(false)}
              onConfirm={handleScheduleEvent}
            />
            <Modal
              open={showEditModal}
              title="Editar Evento"
              footer={null}
              onCancel={() => setShowEditModal(false)}
              destroyOnClose
            >
              <EventEditModal
                event={selectedEvent}
                onSuccess={handleEditSuccess}
              />
            </Modal>
            <Modal
              open={showDeleteModal}
              title="Eliminar Evento"
              onCancel={() => setShowDeleteModal(false)}
              onOk={confirmDelete}
              okText="Eliminar"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
            >
              {selectedEvent && !selectedEvent.is_unique && !selectedEvent.is_edited ? (
                <>
                  <p>¿Qué deseas eliminar?</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label>
                      <input type="radio" name="deleteScope" value="one" checked={deleteScope === 'one'} onChange={() => setDeleteScope('one')} />
                      {' '}Solo esta ocurrencia
                    </label>
                    <label>
                      <input type="radio" name="deleteScope" value="from_now" checked={deleteScope === 'from_now'} onChange={() => setDeleteScope('from_now')} />
                      {' '}Esta y todas las siguientes
                    </label>
                  </div>
                </>
              ) : (
                <p>¿Estás seguro de que deseas eliminar este evento?</p>
              )}
              {selectedEvent && (
                <p style={{ marginTop: 12, color: '#888' }}>
                  Evento: <strong>{selectedEvent.name}</strong> — {selectedEvent.start_date?.tz(dayjs.tz.guess()).format('dddd D [de] MMMM, HH:mm')}
                </p>
              )}
            </Modal>
          </>
        )}
      </div>
    )
  );
};

export default NextSessions;
